import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal, type FormModalTone } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import {
  CategorizedReasons,
  ReasonOption,
  reasonsAPI,
} from '@/services/api/reasons';

export interface BulkReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (notes: string, reasonCode: string) => Promise<void> | void;
  isLoading?: boolean;
  count: number;
  /** Header copy. */
  title: string;
  description: string;
  /** Affirmative button label, e.g. "Tandai Failed". */
  confirmLabel: string;
  /** Reason-fetch filter: which state to query. */
  state: 'PRE_PICKUP' | 'POST_PICKUP' | 'IN_RETURN' | 'UNCATEGORIZED';
  /** Reason-fetch filter: which type to query. */
  type: 'FAILED' | 'COMPLETION' | 'CANCELLATION' | 'RETURN';
  /** Optional actor exclusion CSV (e.g. for Cancel: 'CLIENT,SENDER,RECIPIENT'). */
  excludeActors?: string;
  /** Header icon + tone. */
  icon?: string;
  iconTone?: FormModalTone;
  /** Style the confirm button as destructive. */
  variant?: 'primary' | 'danger';
}

const labelCls = 'mb-1 block text-[11px] font-medium text-text-sub-600';

/**
 * Bulk reason modal — fetches categorized reasons on open, lets the user
 * pick one, and (for "Lainnya" / "Others") collects free-text notes.
 * Mirrors backoffice's `ConfirmationWithReasonModal`.
 */
export const BulkReasonModal: React.FC<BulkReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  count,
  title,
  description,
  confirmLabel,
  state,
  type,
  excludeActors,
  icon = 'ri-question-answer-line',
  iconTone = 'primary',
  variant = 'primary',
}) => {
  const [reasons, setReasons] = useState<CategorizedReasons>({});
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ReasonOption | null>(
    null
  );
  const [customNotes, setCustomNotes] = useState('');

  // Reset state + fetch reasons whenever the modal opens.
  useEffect(() => {
    if (!isOpen) {
      setSelectedReason(null);
      setCustomNotes('');
      setReasons({});
      return;
    }
    setLoadingReasons(true);
    reasonsAPI
      .getCategorized({
        state,
        type,
        language: 'ID',
        ...(excludeActors ? { excludeActors } : {}),
      })
      .then((data) => setReasons(data))
      .catch(() => setReasons({}))
      .finally(() => setLoadingReasons(false));
  }, [isOpen, state, type, excludeActors]);

  const isOther =
    selectedReason?.internalReason?.toLowerCase().includes('lainnya') ||
    selectedReason?.internalReason?.toLowerCase().includes('others') ||
    selectedReason?.internalReason?.toLowerCase().includes('other');

  const canSubmit =
    selectedReason !== null &&
    !loadingReasons &&
    (!isOther || customNotes.trim().length >= 3);

  const handleSubmit = () => {
    if (!selectedReason) return;
    const notes = isOther ? customNotes.trim() : selectedReason.internalReason;
    onConfirm(notes, selectedReason.code);
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className={icon} />}
      iconTone={iconTone}
      title={title}
      subtitle={`${count} ${count === 1 ? 'delivery' : 'deliveries'} selected · ${description}`}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            tone={variant === 'danger' ? 'destructive' : 'primary'}
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Processing…' : confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="flex-1 border-t border-stroke-soft-200" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
            Choose a reason
          </span>
          <div className="flex-1 border-t border-stroke-soft-200" />
        </div>

        {loadingReasons ? (
          <div className="flex items-center justify-center py-8 text-text-soft-400">
            <i className="ri-loader-4-line animate-spin text-lg mr-2" />
            <span className="text-sm">Loading reasons…</span>
          </div>
        ) : Object.keys(reasons).length === 0 ? (
          <p className="text-sm text-text-soft-400 py-4 text-center">
            No reasons available for this case.
          </p>
        ) : (
          <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
            {Object.entries(reasons).map(([category, options]) => (
              <div key={category}>
                <p className={labelCls}>{category}</p>
                <div className="space-y-2 ml-1">
                  {options.map((opt) => (
                    <label
                      key={opt.id}
                      className="flex items-start gap-2.5 cursor-pointer group"
                    >
                      <input
                        type="radio"
                        name="bulk-reason"
                        value={String(opt.id)}
                        checked={selectedReason?.id === opt.id}
                        onChange={() => {
                          setSelectedReason(opt);
                          setCustomNotes('');
                        }}
                        className="mt-0.5 accent-accent shrink-0"
                      />
                      <span className="text-sm text-text-sub-600 group-hover:text-text-strong-950 leading-snug">
                        {opt.internalReason}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {isOther && (
          <div>
            <p className={labelCls}>Additional notes</p>
            <Textarea
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              maxLength={200}
              rows={3}
              placeholder="Briefly explain…"
              className="resize-none"
            />
            <p className="mt-1 text-right text-[11px] text-text-soft-400">
              {customNotes.length}/200
            </p>
          </div>
        )}
      </div>
    </FormModal>
  );
};

export default BulkReasonModal;
